import React, {
  type ComponentProps,
  type ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Flex, message } from 'antd';
import { useTranslation } from 'react-i18next';
import TimePeriodPills from './components/TimePeriodPills';
import AIHistoryTable from './components/AIHistoryTable';
import AIHistoryStatsSection from './components/AIHistoryStatsSection';
import AIHistoryEmptyState from './components/AIHistoryEmptyState';
import ShipmentToolbar from '../Shipments/ShipmentToolbar/ShipmentToolbar';
import ShipmentsFilterPanel from '../Shipments/ControlTower/components/ShipmentFiltersPanel';
import { SentimentKey } from '@/pages/AiHistory/components/SentimentFilter';

import { AIHistoryprops, AIHistoryRecord } from './type';
import { useToken } from '@/common/helper';
import useUnauthorize from '@/hooks/useUnauthorize';
import { useSpin } from '@/components/SpinContext';
import { GetAiHistoryWithPaginationAndSearch } from '@/apis/aiHistory';
import { getRangeText } from '@/common/utils/getRangeText';
import { useAiHistoryFiltersController } from './useHistoryFilterController';
import { AIStateKey } from './components/AIStateFilter';
import { GetAIStateCounts } from '@/apis/aiHistory';
import {
  buildHistoryPayload,
  HistoryPayload,
  toCountsPayload,
} from './utils/buildHistoryPayload';
import { useHistoryKpi } from './useHistoryKpi';
import { CommunicationTypeKey } from './components/TypeFilter';

export interface BaseCountItem<ID extends string> {
  id: ID;
  title: string;
  count: number;
}

export interface BaseCountSection<ID extends string> {
  type: 'section';
  label: string;
  items: BaseCountItem<ID>[];
}

export type RawAIStateSection = BaseCountSection<AIStateKey>;
export type RawSentimentSection = BaseCountSection<SentimentKey>;

type MixedCountId = AIStateKey | SentimentKey;
type CountsSection = BaseCountSection<MixedCountId>;

export interface GetAIStateCountsRes {
  data: {
    is_success: boolean;
    message?: string;
    data: CountsSection[];
  };
}

type ToolbarOverrides = {
  selected: (AIStateKey | SentimentKey | CommunicationTypeKey)[];
  filteredData: AIHistoryRecord[];
  aiHistoryAlertIds: string[];
  aiHistoryEventIds: string[];
  excludedIds: string[];
};

// The JSX toolbar infers `never[]` from its default props; these are the real types
const TypedShipmentToolbar = ShipmentToolbar as ComponentType<
  Omit<ComponentProps<typeof ShipmentToolbar>, keyof ToolbarOverrides> &
    ToolbarOverrides
>;

const AIHistoryPage: React.FC<AIHistoryprops> = ({ title }) => {
  const { t } = useTranslation('aiHistory');
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  // Start loading so the first paint isn't the empty state before the first fetch.
  const [isLoading, setIsLoading] = useState(true);
  const { showSpin, hideSpin } = useSpin();
  const filters = useAiHistoryFiltersController();

  const [period, setPeriod] = useState<string | null>('last_30_days');
  const [sentimentSelected, setSentimentSelected] = useState<SentimentKey[]>(
    []
  );
  const [sentimentSections, setSentimentSections] = useState<
    RawSentimentSection[]
  >([]);

  const {
    kpiData,
    fetchKpi,
    invalidate: invalidateKpi,
  } = useHistoryKpi(token ?? '');

  const [historyList, setHistoryList] = useState<AIHistoryRecord[]>([]);
  const [, setFilteredData] = useState(historyList);
  const [aiStateSelected, setAIStateSelected] = useState<AIStateKey[]>([]);
  const [typeSelected, setTypeSelected] = useState<CommunicationTypeKey[]>([]);

  const [page, setPage] = useState<number>(1);
  const [maxPageSize, setMaxPageSize] = useState<number>(0);
  const [timezoneAbbr, setTimezoneAbbr] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [pageSize] = useState<number>(50);

  const REPORT_PAGE_SIZE = 50;
  const FILTER_DEBOUNCE_MS = 250;

  const [aiStateSections, setAiStateSections] = useState<RawAIStateSection[]>(
    []
  );

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [checked, setChecked] = useState(false);
  const [isSearchTriggered] = useState(false);
  const [selectAllAcrossFilters, setSelectAllAcrossFilters] = useState(false);
  const [excludedIds, setExcludedIds] = useState<string[]>([]);
  const dynamicDropdownHeight = `calc(100vh - 400px)`;
  const [selectedShipmentStatuses, setSelectedShipmentStatuses] = useState<
    Record<string, string>
  >({});

  // Responses from superseded requests are dropped so a slow earlier call can't overwrite a newer one.
  const countsRequestIdRef = useRef(0);
  const listRequestIdRef = useRef(0);

  const fetchAIStateCounts = useCallback(
    (payload: HistoryPayload) => {
      const requestId = ++countsRequestIdRef.current;
      return GetAIStateCounts(token ?? '', toCountsPayload(payload))
        .then((res: GetAIStateCountsRes) => {
          if (requestId !== countsRequestIdRef.current) return;
          if (!res?.data?.is_success) {
            message.error(res?.data?.message || t('errorFetchCounts'));
            return;
          }

          const sections = res.data.data;

          const nextSentimentSections: RawSentimentSection[] = [];
          const nextAiStateSections: RawAIStateSection[] = [];

          sections.forEach((section) => {
            if (section.label === 'Sentiment') {
              nextSentimentSections.push(section as RawSentimentSection);
            } else {
              nextAiStateSections.push(section as RawAIStateSection);
            }
          });

          setAiStateSections(nextAiStateSections);
          setSentimentSections(nextSentimentSections);
        })
        .catch((error) => {
          if (requestId !== countsRequestIdRef.current) return;
          unauthorize(error);
          message.error(t('errorFetchCounts'));
        });
    },
    [token, unauthorize, t]
  );

  // Single source for list, KPIs, counts and select-all mass actions.
  const buildActivePayload = useCallback(
    (search: string) =>
      buildHistoryPayload({
        filtersPayload: filters.filtersPayloadRef.current,
        search,
        period,
        aiState: aiStateSelected,
        sentiment: sentimentSelected,
        communicationType: typeSelected,
      }),
    [
      filters.filtersPayloadRef,
      period,
      aiStateSelected,
      sentimentSelected,
      typeSelected,
    ]
  );

  const fetchAiHistory = useCallback(
    (pageArg: number, search: string) => {
      showSpin();
      setIsLoading(true);
      const payload = buildActivePayload(search);
      const _ = fetchAIStateCounts(payload);
      fetchKpi(payload);

      const body = {
        ...payload,
        page: pageArg,
        page_size: REPORT_PAGE_SIZE,
      };

      const requestId = ++listRequestIdRef.current;
      const isStale = () => requestId !== listRequestIdRef.current;
      GetAiHistoryWithPaginationAndSearch(token ?? '', body)
        .then((res) => {
          if (isStale()) return;
          const data = res?.data?.data;
          if (data?.is_success) {
            setHistoryList(data?.data || []);
            setTotalPages(data?.page_count);
            setMaxPageSize(data?.total_count);
            setTimezoneAbbr(data?.timezone_abbr ?? null);
          } else {
            setHistoryList([]);
            message.error(data?.message || t('errorFetchHistory'));
          }
        })
        .catch((error) => {
          if (isStale()) return;
          setHistoryList([]);
          unauthorize(error);
          message.error(t('errorFetchHistory'));
        })
        .finally(() => {
          if (isStale()) return;
          hideSpin();
          setIsLoading(false);
        });
    },
    [
      showSpin,
      buildActivePayload,
      fetchAIStateCounts,
      fetchKpi,
      token,
      unauthorize,
      hideSpin,
      t,
    ]
  );

  const searchQueryRef = useRef(filters.searchQuery);
  // Bumped by panel searches so the fetch runs after their state updates commit.
  const [fetchNonce, setFetchNonce] = useState(0);

  useEffect(() => {
    searchQueryRef.current = filters.searchQuery;
  }, [filters.searchQuery]);

  // Always call the latest fetch, but don't refetch just because its identity changed
  // (e.g. `t` changes when the app switches to the user's language after load).
  const fetchAiHistoryRef = useRef(fetchAiHistory);
  useEffect(() => {
    fetchAiHistoryRef.current = fetchAiHistory;
  }, [fetchAiHistory]);

  // The filter values the list request depends on; a change here is a real filter change.
  const filtersKey = useMemo(
    () =>
      JSON.stringify({
        period,
        aiStateSelected,
        sentimentSelected,
        typeSelected,
      }),
    [period, aiStateSelected, sentimentSelected, typeSelected]
  );

  // Filter changes wait briefly so rapid multi-select clicks collapse into one set of requests.
  // Responses still in flight for the previous filters are invalidated immediately, so they
  // cannot update the page while the controls already show the new filters.
  const lastFiltersKeyRef = useRef(filtersKey);
  useEffect(() => {
    const filtersChanged = lastFiltersKeyRef.current !== filtersKey;
    lastFiltersKeyRef.current = filtersKey;
    if (!filtersChanged) {
      fetchAiHistoryRef.current(page, searchQueryRef.current);
      return;
    }
    listRequestIdRef.current += 1;
    countsRequestIdRef.current += 1;
    invalidateKpi();
    // Show the loading state during the debounce, not "No Data Found".
    setIsLoading(true);
    const timer = setTimeout(
      () => fetchAiHistoryRef.current(page, searchQueryRef.current),
      FILTER_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [page, filtersKey, fetchNonce, invalidateKpi]);

  type AIHistoryAlertRef = {
    alert_id?: string;
    id?: string;
    eventId?: string;
    notification_type?: string;
  };

  const handleSelect = (
    shipmentId: string,
    isCheckedVal: boolean,
    card: AIHistoryRecord
  ) => {
    if (selectAllAcrossFilters) {
      setExcludedIds((prev) => {
        if (isCheckedVal) {
          return prev.filter((id) => id !== shipmentId);
        }

        if (prev.includes(shipmentId)) return prev;
        return [...prev, shipmentId];
      });

      setSelectedShipmentStatuses((prev) => {
        const updated = { ...prev };

        if (!isCheckedVal) {
          updated[shipmentId] = card?.status;
        } else {
          delete updated[shipmentId];
        }

        return updated;
      });

      return;
    }

    if (isCheckedVal) {
      if (selectedIds.length >= 50) {
        message.error(t('maxShipmentsSelection'));
        return;
      }
      setSelectedIds((prev) => [...prev, shipmentId]);
      setSelectedShipmentStatuses((prev) => ({
        ...prev,
        [shipmentId]: card?.status,
      }));
    } else {
      setSelectedIds((prev) => prev.filter((id) => id !== shipmentId));
      setSelectedShipmentStatuses((prev) => {
        const updated = { ...prev };
        delete updated[shipmentId];
        return updated;
      });
    }
  };

  const selectedRecords = useMemo(() => {
    if (selectAllAcrossFilters) {
      const excluded = new Set(excludedIds);
      return historyList.filter((r) => !excluded.has(r.id));
    }

    const selected = new Set(selectedIds);
    return historyList.filter((r) => selected.has(r.id));
  }, [historyList, selectedIds, selectAllAcrossFilters, excludedIds]);

  const aiStateDescriptions = useMemo(() => {
    return selectedRecords
      .map((r) => (r?.status_info?.description ?? '').trim().toLowerCase())
      .filter(Boolean);
  }, [selectedRecords]);

  const normalizedAiStates = useMemo(() => {
    return aiStateDescriptions.map((d) => {
      if (d.includes('dismiss')) return 'dismissed';
      if (d.includes('resolve')) return 'resolved';
      return 'other';
    });
  }, [aiStateDescriptions]);

  const hasResolvedSelected = useMemo(
    () => normalizedAiStates.some((s) => s === 'resolved'),
    [normalizedAiStates]
  );

  const hasDismissedSelected = useMemo(
    () => normalizedAiStates.some((s) => s === 'dismissed'),
    [normalizedAiStates]
  );

  const selectedAiIds = useMemo(() => {
    const alertIds = new Set<string>();
    const eventIds = new Set<string>();

    selectedRecords.forEach((r) => {
      const a = r?.alert as AIHistoryAlertRef | undefined;
      if (!a) return;

      const isOneTime =
        a.notification_type === 'one_time_notification' || Boolean(a.eventId);

      if (isOneTime) {
        const eid = a.eventId;
        if (eid) eventIds.add(eid);
      } else {
        const aid = a.alert_id;
        if (aid) alertIds.add(aid);
      }
    });

    return { alert_ids: Array.from(alertIds), event_ids: Array.from(eventIds) };
  }, [selectedRecords]);

  const totalCount = maxPageSize;
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = totalCount === 0 ? 0 : Math.min(page * pageSize, totalCount);

  const handleRunSearchFromPanel = useCallback(
    ({
      query,
      page: p,
    }: {
      query: string;
      check: boolean;
      page?: number;
      pageSize?: number;
      calledFrom?: string;
    }) => {
      const nextPage = p ?? 1;
      filters.setSearchQuery(query);
      searchQueryRef.current = query;
      setPage(nextPage);
      setFetchNonce((n) => n + 1);
    },
    [filters]
  );

  return (
    <div className='ai-history-page-wrapper'>
      <p className='zl-heading-24'>{title}</p>

      <div>
        <TimePeriodPills
          selectedPeriod={period}
          onChange={(val) => {
            setPeriod(val);
            setPage(1);
          }}
          showLastWeek
        />
      </div>

      <AIHistoryStatsSection kpiData={kpiData} />

      <div className='ai-history-toolbar'>
        <Flex align='end' wrap gap={12} style={{ marginBottom: 16 }}>
          <p className='zl-heading-20'>AI communications log</p>
          <div className='zl-para-400-12 color-5e'>
            {getRangeText(rangeStart, rangeEnd, totalCount)}
          </div>
        </Flex>

        <Flex
          align='center'
          wrap={false}
          gap={12}
          style={{ width: '100%' }}
          className='ai-history-toolbar-row'
        >
          {historyList?.length > 0 && (
            <TypedShipmentToolbar
              refreshCurrentView={() =>
                fetchAiHistory(page, filters.searchQuery)
              }
              getViewportCalendarIds={() => [] as string[]}
              onApplyQuickFilter={() => {}}
              selected={[
                ...(aiStateSelected ?? []),
                ...(sentimentSelected ?? []),
                ...typeSelected,
              ]}
              isSearchTriggered={isSearchTriggered}
              setSelectedShipmentStatuses={setSelectedShipmentStatuses}
              appliedFilters={filters.appliedFilters}
              selectedShipmentStatuses={selectedShipmentStatuses}
              searchQuery={filters.searchQuery}
              totalShipments={maxPageSize}
              page={page}
              pageSize={pageSize}
              fetchShipments={fetchAiHistory}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              checked={checked}
              setChecked={setChecked}
              setShipmentsData={setHistoryList}
              setFilteredData={setFilteredData}
              filteredData={historyList}
              isAiHistory
              hasAiHistoryResolvedSelected={hasResolvedSelected}
              hasAiHistoryDismissedSelected={hasDismissedSelected}
              aiHistoryAlertIds={selectedAiIds.alert_ids}
              aiHistoryEventIds={selectedAiIds.event_ids}
              selectAllAcrossFilters={selectAllAcrossFilters}
              setSelectAllAcrossFilters={setSelectAllAcrossFilters}
              excludedIds={excludedIds}
              setExcludedIds={setExcludedIds}
              appliedSearchQuery={(filters.searchQuery ?? '').trim()}
              filtersPayload={buildActivePayload(
                (filters.searchQuery ?? '').trim()
              )}
            />
          )}

          <div className='ai-history-filters-col'>
            <ShipmentsFilterPanel
              dropdownMaxHeight={dynamicDropdownHeight}
              sentimentSections={sentimentSections}
              showSentimentFilter={true}
              sentimentSelected={sentimentSelected}
              setSentimentSelected={setSentimentSelected}
              aiStateSections={aiStateSections}
              savedFilterKey={'ai_history'}
              showAiStateFilter={true}
              aiStateSelected={aiStateSelected}
              setAIStateSelected={setAIStateSelected}
              typeSelected={typeSelected}
              setTypeSelected={setTypeSelected}
              token={token ?? ''}
              permissions={undefined}
              appliedFilters={filters.appliedFilters}
              setAppliedFilters={filters.setAppliedFilters}
              filterArray={filters.filterArray}
              setFilterArray={filters.setFilterArray}
              setFiltersPayload={filters.setFiltersPayload}
              searchQuery={filters.searchQuery}
              setSearchQuery={(val) => {
                filters.setSearchQuery(val);
                setPage(1);
              }}
              isSearchVisible={filters.isSearchVisible}
              setIsSearchVisible={filters.setIsSearchVisible}
              page={page}
              pageSize={pageSize}
              onRunSearch={handleRunSearchFromPanel}
              registerHandleApplyFilters={() => {}}
            />
          </div>
        </Flex>
      </div>

      <div className='h-full'>
        {historyList?.length > 0 ? (
          <AIHistoryTable
            onRefresh={() => fetchAiHistory(page, searchQueryRef.current)}
            historyList={historyList}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            totalPages={totalPages}
            maxPageSize={maxPageSize}
            timezoneAbbr={timezoneAbbr}
            checked={checked}
            selectedIds={selectedIds}
            excludedIds={excludedIds}
            selectAllAcrossFilters={selectAllAcrossFilters}
            handleSelect={(shipmentId, isCheckedVal, record) => {
              if (!shipmentId || !record) return;
              handleSelect(shipmentId, Boolean(isCheckedVal), record);
            }}
          />
        ) : (
          <AIHistoryEmptyState
            isEmptyList={historyList.length === 0}
            isLoading={isLoading}
            hasActiveSearchOrFilters={
              (filters.searchQuery ?? '').trim().length > 0 ||
              filters.appliedFilters.length > 0 ||
              sentimentSelected.length > 0 ||
              aiStateSelected.length > 0 ||
              typeSelected.length > 0 ||
              Object.keys(selectedShipmentStatuses).length > 0
            }
          />
        )}
      </div>
    </div>
  );
};

export default AIHistoryPage;
